import { BanIcon, MailIcon } from "@heroicons/react/outline";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";

/**
 * Landing page shown when a soft-deleted SSO identity attempts to
 * sign in. Reactivation requires the IdP admin to provision a new
 * email; the page is unauthenticated and clears any stray session.
 */
export const AccountDeactivatedPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    if (auth?.userToken) {
      auth.logout();
    }
  }, [auth]);

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <BanIcon className="h-8 w-8 text-red-600" strokeWidth={2} />
      </div>

      <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
        This account has been deactivated
      </h1>

      <p className="mb-6 text-base text-gray-600">
        Your access to PromptLayer was removed by your organization administrator. For audit and
        security reasons, reactivation requires a new account.
      </p>

      <div className="mb-8 w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
        <p className="mb-1 text-sm font-semibold text-gray-900">What to do next</p>
        <p className="text-sm text-gray-600">
          Please contact your IT or workspace administrator. They&apos;ll need to provision a new
          email address for you in your identity provider; that new address will then be able to
          sign in here.
        </p>
      </div>

      <a
        href="mailto:support@promptlayer.com?subject=Deactivated%20account%20-%20PromptLayer"
        className="mb-4 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
      >
        <MailIcon className="h-4 w-4" />
        Contact PromptLayer support
      </a>

      <div className="w-full">
        <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
          Back to sign in
        </Button>
      </div>
    </div>
  );
};
